/**
 * Can every dish physically get from its crates to a hatch? A proof from the
 * layout alone — no clocks, no search.
 *
 * `validateMap` asks whether each station a dish needs *exists* somewhere some
 * seat can face, and whether the seats share *a* counter. That is enough to
 * boot, and not enough to cook: a stove only one chef can reach is useless if
 * the meat can never get into that chef's hands. This walks each item the way
 * a round would move it, on (cell, facing):
 *
 *   item   crate -> board (when it is chopped) -> the heat it cooks on
 *   plate  the stack the plates start on -> each heat an item is plated from
 *          -> a hatch
 *   pan    from where it starts -> the heat it cooks on
 *
 * Each step is done by a seat whose region faces the station (`seatRegions`:
 * the flood fill, narrowed where two chefs on one floor can never pass). An
 * item changes hands only where both seats can reach something that holds it:
 * a counter for anything, and a board for a bare ingredient (one chef sets it
 * down, the other chops or picks it up). Putting an item in a pan and plating
 * it out are both done at the heat, so the pan itself is never a crossing.
 *
 * A counter that starts with a pan on it holds nothing until the pan is moved,
 * so it counts only when a seat that faces it can also face somewhere else the
 * pan may rest — a bare counter, or free equipment the pan goes on.
 *
 * The same walk gives the fewest crossings each dish needs, which is the
 * oracle's `forcedHandoffsPerDish` — one implementation for the proof and the
 * metric. And it answers one advisory question: can a dirty plate get from a
 * plate return to a sink and back to a stack? Without that a round serves at
 * most `platesPerMap` dishes; it boots and plays, so it is advice.
 *
 * Reads the recipe book, `platesPerMap` (wording only) and the map. Browser
 * safe: the editor runs it on every keystroke.
 */

import type { ContainerKind, IngredientId, StationType } from '@overcooked/shared'
import { cellKey } from '@overcooked/shared'
import type { Content, CookableDef, MapContent, TransformDef } from './content'
import type { SeatRegion } from './regions'
import { seatRegions } from './regions'

/** One dish's verdict. */
export interface DishFlow {
  dish: string
  /** Every item and its plate can reach a hatch. */
  ok: boolean
  /**
   * The fewest times the dish's items and plate must change hands between
   * chefs, from the layout alone. `null` when the dish cannot be made, or the
   * recipe book describes it in a way this walk does not model.
   */
  forcedHandoffs: number | null
  /** Why not, as a sentence a map author can act on. `null` when it flows. */
  problem: string | null
}

export interface ItemFlow {
  /** Every dish asked about flows. */
  ok: boolean
  dishes: DishFlow[]
  /** A dirty plate can get return -> sink -> plate stack. */
  recycles: boolean
  /** Says which, either way. */
  recycleDetail: string
  /** The regions the walk used, seat order. */
  regions: SeatRegion[]
}

const words = (type: string): string => type.replace(/_/g, ' ')
const article = (noun: string): string => (/^[aeiou]/.test(noun) ? `an ${noun}` : `a ${noun}`)

/** "seat 1", "seats 1 and 2", "seats 1, 2 and 3". */
function seatWords(seats: number[]): string {
  const named = seats.map((i) => String(i + 1))
  if (named.length === 1) return `seat ${named[0]}`
  return `seats ${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`
}

/**
 * The fewest hand-offs between each pair of seats: 0 for the same seat, 1
 * when some link cell faces both regions, more through a third seat,
 * Infinity when nothing connects them.
 */
function hopTable(regions: SeatRegion[], links: readonly string[]): number[][] {
  const n = regions.length
  const linked = (a: number, b: number): boolean =>
    links.some((key) => regions[a].faced.has(key) && regions[b].faced.has(key))
  const out: number[][] = []
  for (let a = 0; a < n; a += 1) {
    const row = new Array<number>(n).fill(Number.POSITIVE_INFINITY)
    row[a] = 0
    let frontier = [a]
    let depth = 0
    while (frontier.length > 0) {
      depth += 1
      const next: number[] = []
      for (const s of frontier) {
        for (let t = 0; t < n; t += 1) {
          if (row[t] !== Number.POSITIVE_INFINITY || !linked(s, t)) continue
          row[t] = depth
          next.push(t)
        }
      }
      frontier = next
    }
    out.push(row)
  }
  return out
}

interface ChainResult {
  /** The cost of ending at each seat. */
  cost: number[]
  /** The first stage no seat could reach, or null when the chain completes. */
  failedAt: number | null
  /** At `failedAt`: the seats holding the thing after the stage before. */
  holders: number[]
}

/**
 * Do `stages` in order: stage `i` at any of its cells, by a seat facing it;
 * between stages the thing moves between seats at `hops` cost each.
 */
function chain(regions: SeatRegion[], hops: number[][], stages: readonly (readonly string[])[]): ChainResult {
  const n = regions.length
  const faces = (seat: number, stage: readonly string[]): boolean => stage.some((key) => regions[seat].faced.has(key))
  let cost = new Array<number>(n).fill(Number.POSITIVE_INFINITY)
  for (let i = 0; i < stages.length; i += 1) {
    const next = new Array<number>(n).fill(Number.POSITIVE_INFINITY)
    for (let s = 0; s < n; s += 1) {
      if (!faces(s, stages[i])) continue
      if (i === 0) {
        next[s] = 0
        continue
      }
      for (let p = 0; p < n; p += 1) next[s] = Math.min(next[s], cost[p] + hops[p][s])
    }
    if (next.every((c) => c === Number.POSITIVE_INFINITY)) {
      const holders = cost.flatMap((c, seat) => (c < Number.POSITIVE_INFINITY ? [seat] : []))
      return { cost: next, failedAt: i, holders }
    }
    cost = next
  }
  return { cost, failedAt: null, holders: [] }
}

const least = (costs: number[]): number => Math.min(Number.POSITIVE_INFINITY, ...costs)

function permutations<T>(items: T[]): T[][] {
  if (items.length <= 1) return [items]
  const out: T[][] = []
  items.forEach((item, i) => {
    for (const rest of permutations([...items.slice(0, i), ...items.slice(i + 1)])) out.push([item, ...rest])
  })
  return out
}

/**
 * Whether every dish in `dishIds` can be carried from crate to hatch on this
 * layout, the fewest hand-offs each needs, and whether plates recycle.
 *
 * `regions` defaults to `seatRegions(map)`; the oracle passes its own, which
 * are the same regions computed once.
 */
export function itemFlow(
  content: Content,
  map: MapContent,
  dishIds: readonly string[],
  regions: SeatRegion[] = seatRegions(map),
): ItemFlow {
  const { recipes } = content
  const n = regions.length
  const tiles = Object.values(map.tiles)
  const cellsOf = (type: StationType): string[] => tiles.filter((t) => t.type === type).map((t) => cellKey(t.cell))
  const faced = (key: string): boolean => regions.some((r) => r.faced.has(key))
  const seatsFacing = (keys: readonly string[]): number[] =>
    regions.flatMap((r, seat) => (keys.some((key) => r.faced.has(key)) ? [seat] : []))
  const label = (id: IngredientId): string => (recipes.ingredients[id]?.label ?? words(id)).toLowerCase()

  // -- where things can change hands ---------------------------------------

  const starting = new Map<string, ContainerKind>()
  for (const placement of map.containers ?? []) starting.set(cellKey(placement.cell), placement.kind)
  const counters = cellsOf('counter')
  /** A counter carrying a starting container holds nothing until a seat facing it can set the container down elsewhere. */
  const usable = (key: string): boolean => {
    const kind = starting.get(key)
    if (kind === undefined) return true
    const equipment = recipes.containers[kind]?.equipment ?? []
    const elsewhere = [
      ...counters.filter((other) => other !== key && !starting.has(other)),
      ...equipment.flatMap((type) => cellsOf(type)).filter((other) => !starting.has(other)),
    ]
    return regions.some((r) => r.faced.has(key) && elsewhere.some((other) => r.faced.has(other)))
  }
  const counterLinks = counters.filter(usable)
  /** Anything carryable crosses on a counter. */
  const hopsAny = hopTable(regions, counterLinks)
  /** A bare ingredient can also be set on a board by one chef and taken off by the other. */
  const hopsIngredient = hopTable(regions, [...counterLinks, ...cellsOf('board')])

  /** A container of `kind` can be got onto the heat at `heat`. */
  const containerReaches = (kind: ContainerKind, heat: string): boolean =>
    (map.containers ?? [])
      .filter((p) => p.kind === kind)
      .some((p) => {
        const from = cellKey(p.cell)
        if (from === heat) return true
        for (let a = 0; a < n; a += 1) {
          if (!regions[a].faced.has(from)) continue
          for (let b = 0; b < n; b += 1) {
            if (regions[b].faced.has(heat) && hopsAny[a][b] < Number.POSITIVE_INFINITY) return true
          }
        }
        return false
      })

  // -- the plates ----------------------------------------------------------

  // Only the first stack in reading order starts with plates (`initialState`).
  const stocked = tiles.find((t) => t.type === 'plate_stack')
  const stack = stocked ? cellKey(stocked.cell) : null
  const hatches = cellsOf('hatch')

  // -- each dish -----------------------------------------------------------

  const dishes: DishFlow[] = dishIds.map((dishId): DishFlow => {
    const fail = (problem: string): DishFlow => ({ dish: dishId, ok: false, forcedHandoffs: null, problem: `${dishId}: ${problem}` })
    const dish = recipes.dishes.find((d) => d.id === dishId)
    if (!dish) return fail('the recipe book has no such dish')
    if (dish.contents.length === 0) return { dish: dishId, ok: true, forcedHandoffs: 0, problem: null }

    const cooks = dish.contents.map((cooked) => recipes.cookable.find((c) => c.to === cooked) ?? null)
    // A dish item nothing cooks is not something this walk models; say
    // nothing rather than claim a proof it does not have.
    if (cooks.some((cook) => cook === null)) return { dish: dishId, ok: true, forcedHandoffs: null, problem: null }
    const routes = (cooks as CookableDef[]).map((cook) => {
      const input = cook.from
      const chop: TransformDef | null = recipes.transforms.find((t) => t.to === input) ?? null
      const raw = chop ? chop.from : input
      const crates = tiles.filter((t) => t.type === 'crate' && t.dispenses === raw).map((t) => cellKey(t.cell))
      const boards = chop ? cellsOf(chop.station) : null
      const heats = cellsOf(cook.station)
      return { cook, input, chop, raw, crates, boards, heats }
    })

    // Existence and reach, item by item, in the order a chef meets them.
    const usableHeats: string[][] = []
    for (const r of routes) {
      const { cook } = r
      if (r.crates.length === 0) return fail(`there is no ${label(r.raw)} crate`)
      if (!r.crates.some(faced)) return fail(`no seat can reach a ${label(r.raw)} crate — put one next to a floor cell a seat can stand on`)
      if (r.chop && r.boards) {
        if (r.boards.length === 0) return fail(`there is no ${words(r.chop.station)} to cut the ${label(r.raw)} on`)
        if (!r.boards.some(faced)) return fail(`no seat can reach a ${words(r.chop.station)} — put one next to a floor cell a seat can stand on`)
      }
      if (r.heats.length === 0) return fail(`there is no ${words(cook.station)}`)
      if (!r.heats.some(faced)) return fail(`no seat can reach ${article(words(cook.station))} — put one next to a floor cell a seat can stand on`)
      const heats = r.heats.filter((h) => faced(h) && containerReaches(cook.container, h))
      if (heats.length === 0) {
        return fail(`no ${cook.container} can be brought to ${article(words(cook.station))} a seat can reach`)
      }
      usableHeats.push(heats)
    }
    if (stack === null) return fail('there is no plate stack')
    if (!faced(stack)) return fail('no seat can reach the plate stack the plates start on — put it next to a floor cell a seat can stand on')
    if (hatches.length === 0) return fail('there is no hatch')
    if (!hatches.some(faced)) return fail('no seat can reach a hatch — put one next to a floor cell a seat can stand on')

    // Each item to its heat. Diagnosed over every usable heat at once: if no
    // heat at all can be reached, no single choice of heat can.
    for (let i = 0; i < routes.length; i += 1) {
      const r = routes[i]
      const stages = [r.crates, ...(r.boards ? [r.boards] : []), usableHeats[i]]
      const walked = chain(regions, hopsIngredient, stages)
      if (walked.failedAt === null) continue
      const atBoard = r.chop !== null && walked.failedAt === 1
      const what = atBoard ? label(r.raw) : label(r.input)
      const where = words(atBoard && r.chop ? r.chop.station : r.cook.station)
      const users = seatsFacing(stages[walked.failedAt])
      return fail(
        `the ${what} only ever reaches ${seatWords(walked.holders)}, and only ${seatWords(users)} can use ` +
          `${article(where)} — nothing both can reach (a counter, or a board) can pass it across; ` +
          'add a counter both seats can face',
      )
    }
    // The plate, past every heat, to a hatch: the same relaxation first.
    {
      const stages = [[stack], ...usableHeats, hatches]
      const walked = chain(regions, hopsAny, stages)
      if (walked.failedAt !== null) {
        const last = walked.failedAt === stages.length - 1
        const where = last ? 'a hatch' : article(words(routes[walked.failedAt - 1].cook.station))
        return fail(
          `a plate only ever reaches ${seatWords(walked.holders)}, and only ${seatWords(seatsFacing(stages[walked.failedAt]))} ` +
            `can use ${where} — no counter both can reach can pass it across; add a counter both seats can face`,
        )
      }
    }

    // Exact: every choice of heat for each item, every order of plating.
    let choices: string[][] = [[]]
    for (const heats of usableHeats) choices = choices.flatMap((picked) => heats.map((h) => [...picked, h]))
    let best = Number.POSITIVE_INFINITY
    for (const heats of choices) {
      let items = 0
      for (let i = 0; i < routes.length && items < best; i += 1) {
        const r = routes[i]
        const stages = r.boards ? [r.crates, r.boards, [heats[i]]] : [r.crates, [heats[i]]]
        items += least(chain(regions, hopsIngredient, stages).cost)
      }
      if (items >= best) continue
      let plate = Number.POSITIVE_INFINITY
      for (const order of permutations(heats)) {
        plate = Math.min(plate, least(chain(regions, hopsAny, [[stack], ...order.map((h) => [h]), hatches]).cost))
      }
      best = Math.min(best, items + plate)
    }
    if (!Number.isFinite(best)) {
      return fail(
        'its items and a plate never meet at a heat both can be brought to — add a counter both seats can face',
      )
    }
    return { dish: dishId, ok: true, forcedHandoffs: best, problem: null }
  })

  // -- plates coming back --------------------------------------------------

  // The editor is not sent `platesPerMap` (it is not a rule it checks), so the
  // sentence names the knob when it has no number for it.
  const atMost =
    typeof content.timings?.platesPerMap === 'number'
      ? `at most ${content.timings.platesPerMap} serves (platesPerMap)`
      : 'at most platesPerMap serves — the plates it starts with'
  const returns = cellsOf('plate_return')
  const sinks = cellsOf('sink')
  const stacks = cellsOf('plate_stack')
  const recycles =
    returns.length > 0 &&
    sinks.length > 0 &&
    stacks.length > 0 &&
    chain(regions, hopsAny, [returns, sinks, stacks]).failedAt === null
  const recycleDetail = recycles
    ? 'a dirty plate can go plate return → sink → plate stack'
    : `no way from a plate return to a sink and back to a plate stack, so a round can make ${atMost}; ` +
      'put the return, a sink and the stack where the seats can pass plates between them'

  return { ok: dishes.every((d) => d.ok), dishes, recycles, recycleDetail, regions }
}
